function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useState } from 'react';
import * as RadixAccordion from '@radix-ui/react-accordion';
import { Typography } from 'components/Typography/Typography';
import { AccordionContextProvider, useAccordionContext } from './AccordionContext';
import * as S from './Accordion.styles';
const Accordion = /*#__PURE__*/React.forwardRef(({
  variant = 'outlined',
  type = 'single',
  children,
  disabled,
  ...accordionProps
}, ref) => {
  const singleProps = accordionProps;
  const multipleProps = accordionProps;
  return /*#__PURE__*/React.createElement(AccordionContextProvider, {
    variant: variant,
    disabled: disabled
  }, /*#__PURE__*/React.createElement(S.AccordionWrapper, {
    $disabled: disabled
  },
  // Because Radix: https://github.com/radix-ui/primitives/blob/main/packages/react/accordion/src/Accordion.tsx#L50C33-L50C33
  type === 'multiple' ? /*#__PURE__*/React.createElement(AccordionMultiple, _extends({
    ref: ref,
    disabled: disabled
  }, multipleProps), children) : /*#__PURE__*/React.createElement(AccordionSingle, _extends({
    ref: ref,
    disabled: disabled
  }, singleProps), children)));
});
const AccordionSingle = /*#__PURE__*/React.forwardRef(({
  disabled,
  defaultValue,
  value,
  onValueChange,
  children,
  collapsible = true
}, ref) => {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const handleValueChange = v => {
    setInternalValue(v);
    if (onValueChange) {
      onValueChange(v);
    }
  };
  if (disabled && (value || internalValue)) {
    setInternalValue('');
    if (onValueChange) {
      onValueChange('');
    }
  }
  return /*#__PURE__*/React.createElement(S.AccordionRoot, {
    type: "single",
    collapsible: collapsible,
    disabled: disabled,
    ref: ref,
    value: value ?? internalValue,
    onValueChange: handleValueChange
  }, children);
});
const AccordionMultiple = /*#__PURE__*/React.forwardRef(({
  disabled,
  defaultValue,
  value,
  onValueChange,
  children
}, ref) => {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const isControlled = !!value;
  const handleValueChange = v => {
    setInternalValue(v);
    if (onValueChange) {
      onValueChange(v);
    }
  };
  if (disabled && !isControlled && internalValue?.length) {
    setInternalValue([]);
  }
  return /*#__PURE__*/React.createElement(S.AccordionRoot, {
    type: "multiple",
    disabled: disabled,
    ref: ref,
    value: value ?? internalValue,
    onValueChange: handleValueChange
  }, children);
});
const AccordionItem = /*#__PURE__*/React.forwardRef(({
  children,
  ...props
}, ref) => {
  return /*#__PURE__*/React.createElement(RadixAccordion.Item, _extends({
    ref: ref
  }, props), children);
});
const AccordionTrigger = /*#__PURE__*/React.forwardRef(({
  children
}, ref) => {
  const {
    variant
  } = useAccordionContext();
  return /*#__PURE__*/React.createElement(RadixAccordion.Header, null, /*#__PURE__*/React.createElement(S.AccordionTrigger, {
    ref: ref,
    $variant: variant
  }, typeof children === 'string' ? /*#__PURE__*/React.createElement(Typography, {
    variant: "ui",
    size: variant === 'outlined' ? 'large' : 'medium',
    weight: "bold"
  }, children) : children, /*#__PURE__*/React.createElement(S.AccordionCaret, null)));
});
const AccordionContent = /*#__PURE__*/React.forwardRef(({
  children
}, ref) => {
  const {
    variant
  } = useAccordionContext();
  return /*#__PURE__*/React.createElement(S.AccordionContent, {
    ref: ref,
    $variant: variant
  }, typeof children === 'string' ? /*#__PURE__*/React.createElement(Typography, {
    variant: "ui",
    size: variant === 'outlined' ? 'large' : 'medium'
  }, children) : children);
});
export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };